// The roll-up. Two rankings, kept apart on purpose: a product can live on
// search healthily across many terms, or barely use search and hang on one.
// Merging them into one list would hide whichever case the seller has.
import React from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import s from '../SearchAttribution.module.css';
import { money, num, pct } from '../format';

export default function AccountView({ data, loading, threshold }) {
  if (loading && !data) return <div className={s.empty}>Loading…</div>;
  const a = data?.account;
  if (!a) return <div className={s.empty}>No attribution data for this window.</div>;

  return (
    <>
      <div className={s.figures} style={{ marginBottom: 18 }}>
        <div className={s.figure}>
          <span className={s.figureLabel}>Revenue from search</span>
          <span className={s.figureValue}>{money(a.search_revenue)}</span>
          <span className={s.basis}>Amazon’s figure</span>
        </div>
        <div className={s.figure}>
          <span className={s.figureLabel}>Share of revenue</span>
          <span className={s.figureValue}>{pct(a.share_of_revenue, 1)}</span>
          <span className={s.figureNote}>
            over the {num(a.share_measured_on)} products that earned on both sides
          </span>
        </div>
        <div className={s.figure}>
          <span className={s.figureLabel}>Products reported</span>
          <span className={s.figureValue}>{num(a.search_revenue_reported)}</span>
          <span className={s.figureNote}>
            {a.missing_totals > 0
              ? `${num(a.missing_totals)} have search revenue but no total to compare it against`
              : 'every one has a total to compare against'}
          </span>
        </div>
        <div className={s.figure}>
          <span className={s.figureLabel}>Dependency flags</span>
          <span className={s.figureValue}>{num(a.flagged)}</span>
          <span className={s.figureNote}>
            above {pct(threshold, 0)} of a product&apos;s own search clicks on one term.
            Against total sessions the same term is a far smaller slice — shown per product.
          </span>
        </div>
      </div>

      <div className={s.twoUp}>
        <section>
          <h4 className={s.expansionTitle}>Products by search share</h4>
          {a.by_search_share?.length ? (
            <table className={s.subTable}>
              <thead>
                <tr><th>ASIN</th><th className={s.right}>Search share</th>
                  <th className={s.right}>From search</th><th className={s.right}>Total</th></tr>
              </thead>
              <tbody>
                {a.by_search_share.map((p) => (
                  <tr key={p.asin}>
                    <td>{p.asin}</td>
                    <td className={s.right}>{pct(p.share_of_revenue, 1)}</td>
                    <td className={s.right}>{money(p.search_revenue)}</td>
                    <td className={s.right}>{money(p.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={s.hint}>
              No product reports both a search revenue and a total revenue for this window, so
              no share can be taken.
            </p>
          )}
        </section>

        <section>
          <h4 className={s.expansionTitle}>Products by dependency</h4>
          {a.by_dependency?.length ? (
            <table className={s.subTable}>
              <thead>
                <tr><th>ASIN</th><th className={s.right}>On one term</th><th>That term</th>
                  <th className={s.right}>Search clicks</th></tr>
              </thead>
              <tbody>
                {a.by_dependency.map((d) => (
                  <tr key={d.asin}>
                    <td>
                      {d.asin}
                      {d.flagged && <span className={s.flagInline}><FiAlertTriangle /></span>}
                    </td>
                    <td className={s.right}>{pct(d.share, 0)}</td>
                    <td>{d.term}</td>
                    <td className={s.right}>{num(d.search_clicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={s.hint}>
              No product has enough search clicks behind it for a dependency share to mean
              anything. One search click makes a product 100% dependent on the term it came
              from, which is true and describes no risk.
            </p>
          )}
        </section>
      </div>
    </>
  );
}
